import { supabase } from '@/integrations/supabase/client';
import type {
  RulesEngineInput,
  RulesEngineOutput,
  PayerAuthRule,
  PayerRuleTemplate,
  DocumentType,
  DocumentRequirement,
  MatchedRule,
  AuthRequirement,
  SubmissionLaneType,
} from './types';

// Default fallback values when no rules match
const DEFAULT_OUTPUT: Omit<RulesEngineOutput, 'evaluation_timestamp' | 'matched_rules'> = {
  auth_required: 'required',
  required_documents: [],
  recommended_lane: 'portal',
  sla_hours: 336, // 14 days
  initial_follow_up_hours: 72,
  follow_up_cadence_hours: 48,
};

// CPT code patterns that typically don't require auth
const NO_AUTH_CPT_PATTERNS = [
  /^99[0-4]\d\d$/, // E&M codes 99000-99499
  /^9920[1-5]$/, // Office visits
  /^9921[1-5]$/, // Office visits established
];

/**
 * Evaluates payer rules for a given authorization request
 * This is the core rules engine that powers both surgeon console and ops queue
 */
export async function evaluateRules(input: RulesEngineInput): Promise<RulesEngineOutput> {
  const startTime = Date.now();
  const matchedRules: MatchedRule[] = [];
  let aggregatedDocuments: string[] = [];
  let specialInstructions: string[] = [];
  let clinicalCriteria: string[] = [];
  
  // Determine overall auth requirement (most restrictive wins)
  let overallAuthRequired: AuthRequirement = 'not_required';
  let authConditionDescription: string | undefined;
  let recommendedLane: SubmissionLaneType = 'portal';
  let fallbackLane: SubmissionLaneType | undefined;
  let slaHours = DEFAULT_OUTPUT.sla_hours;
  let initialFollowUp = DEFAULT_OUTPUT.initial_follow_up_hours;
  let followUpCadence = DEFAULT_OUTPUT.follow_up_cadence_hours;

  try {
    // Fetch matching rules for each CPT code
    for (const cptCode of input.cpt_codes) {
      const modifier = input.modifiers?.find((m, i) => i === input.cpt_codes.indexOf(cptCode));
      
      // Build query for specific rules
      let query = supabase
        .from('payer_auth_rules')
        .select('*')
        .eq('cpt_code', cptCode)
        .eq('injury_type', input.injury_type)
        .eq('is_active', true)
        .lte('effective_date', new Date().toISOString().split('T')[0]);

      // Add payer filter
      if (input.payer_id) {
        query = query.or(`payer_id.eq.${input.payer_id},payer_id.is.null`);
      }

      // Add site of service filter if provided
      if (input.site_of_service) {
        query = query.or(`site_of_service.eq.${input.site_of_service},site_of_service.is.null`);
      }

      // Add modifier filter if provided
      if (modifier) {
        query = query.or(`modifier.eq.${modifier},modifier.is.null`);
      }

      const { data: rules, error } = await query
        .order('payer_id', { ascending: false, nullsFirst: false }) // Specific payer rules first
        .order('site_of_service', { ascending: false, nullsFirst: false })
        .limit(5);

      if (error) {
        console.error('Error fetching rules:', error);
        continue;
      }

      if (rules && rules.length > 0) {
        // Use the most specific matching rule
        const bestRule = rules[0] as unknown as PayerAuthRule;
        
        matchedRules.push({
          rule_id: bestRule.id,
          cpt_code: cptCode,
          modifier,
          auth_requirement: bestRule.auth_requirement,
          source: 'specific_rule',
        });

        // Aggregate requirements
        if (bestRule.auth_requirement === 'required') {
          overallAuthRequired = 'required';
        } else if (bestRule.auth_requirement === 'conditional' && overallAuthRequired !== 'required') {
          overallAuthRequired = 'conditional';
          authConditionDescription = bestRule.auth_condition_description;
        }

        // Collect documents
        if (bestRule.required_documents) {
          aggregatedDocuments = [...new Set([...aggregatedDocuments, ...bestRule.required_documents])];
        }

        // Update submission lane (prefer FHIR PAS if any rule supports it)
        if (bestRule.primary_submission_lane === 'fhir_pas') {
          recommendedLane = 'fhir_pas';
          fallbackLane = bestRule.fallback_submission_lane;
        } else if (recommendedLane !== 'fhir_pas') {
          recommendedLane = bestRule.primary_submission_lane;
          fallbackLane = bestRule.fallback_submission_lane;
        }

        // Use most restrictive SLA
        if (input.urgency === 'urgent' || input.urgency === 'emergent') {
          slaHours = Math.min(slaHours, bestRule.urgent_sla_hours || bestRule.sla_hours);
        } else {
          slaHours = Math.min(slaHours, bestRule.sla_hours);
        }

        initialFollowUp = Math.min(initialFollowUp, bestRule.initial_follow_up_hours);
        followUpCadence = Math.min(followUpCadence, bestRule.follow_up_cadence_hours);

        if (bestRule.special_instructions) {
          specialInstructions.push(bestRule.special_instructions);
        }
        if (bestRule.clinical_criteria) {
          clinicalCriteria.push(bestRule.clinical_criteria);
        }
      } else {
        // No specific rule found - check if CPT typically requires auth
        const requiresAuth = !NO_AUTH_CPT_PATTERNS.some(pattern => pattern.test(cptCode));
        
        matchedRules.push({
          rule_id: 'default',
          cpt_code: cptCode,
          modifier,
          auth_requirement: requiresAuth ? 'required' : 'not_required',
          source: 'default',
        });

        if (requiresAuth && overallAuthRequired !== 'required') {
          overallAuthRequired = 'required';
        }
      }
    }

    // If no specific rules found, fall back to template
    if (matchedRules.every(r => r.source === 'default')) {
      const template = await getDefaultTemplate(input.injury_type);
      if (template) {
        overallAuthRequired = template.auth_requirement;
        aggregatedDocuments = template.required_documents;
        recommendedLane = template.primary_submission_lane;
        fallbackLane = template.fallback_submission_lane ?? undefined;
        slaHours = template.sla_hours;
        initialFollowUp = template.initial_follow_up_hours;
        followUpCadence = template.follow_up_cadence_hours;
      }
    }

    // Fetch document type details
    const documentRequirements = await getDocumentRequirements(aggregatedDocuments);

    // Log evaluation for audit
    await logEvaluation(input, matchedRules, overallAuthRequired, aggregatedDocuments, recommendedLane, slaHours, initialFollowUp, Date.now() - startTime);

    return {
      auth_required: overallAuthRequired,
      auth_condition_description: authConditionDescription,
      required_documents: documentRequirements,
      recommended_lane: recommendedLane,
      fallback_lane: fallbackLane,
      sla_hours: slaHours,
      sla_deadline: new Date(Date.now() + slaHours * 60 * 60 * 1000),
      initial_follow_up_hours: initialFollowUp,
      follow_up_cadence_hours: followUpCadence,
      matched_rules: matchedRules,
      special_instructions: specialInstructions.length > 0 ? specialInstructions : undefined,
      clinical_criteria: clinicalCriteria.length > 0 ? clinicalCriteria : undefined,
      evaluation_timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Rules engine error:', error);
    
    // Return safe defaults on error
    return {
      ...DEFAULT_OUTPUT,
      matched_rules: [],
      evaluation_timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Get the default template for an injury type
 */
async function getDefaultTemplate(injuryType: 'bwc' | 'commercial'): Promise<PayerRuleTemplate | null> {
  const templateName = injuryType === 'bwc' ? 'Ohio BWC Standard' : 'Commercial Standard';
  
  const { data, error } = await supabase
    .from('payer_rule_templates')
    .select('*')
    .eq('name', templateName)
    .eq('is_system_template', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching template:', error);
    return null;
  }

  return data as unknown as PayerRuleTemplate | null;
}

/**
 * Fetch document type details and build requirement list
 */
async function getDocumentRequirements(documentCodes: string[]): Promise<DocumentRequirement[]> {
  if (documentCodes.length === 0) return [];

  const { data: docTypes, error } = await supabase
    .from('document_types')
    .select('*')
    .in('code', documentCodes);

  if (error) {
    console.error('Error fetching document types:', error);
    return documentCodes.map(code => ({
      code,
      name: code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      is_required: true,
      is_conditional: false,
      is_provided: false,
    }));
  }

  const docTypeMap = new Map((docTypes as DocumentType[]).map(d => [d.code, d]));

  return documentCodes.map(code => {
    const docType = docTypeMap.get(code);
    return {
      code,
      name: docType?.name || code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: docType?.description,
      category: docType?.category,
      is_required: true,
      is_conditional: false,
      is_provided: false,
    };
  });
}

/**
 * Log rule evaluation for audit trail
 */
async function logEvaluation(
  input: RulesEngineInput,
  matchedRules: MatchedRule[],
  authRequirement: AuthRequirement,
  requiredDocuments: string[],
  recommendedLane: SubmissionLaneType,
  slaHours: number,
  followUpHours: number,
  durationMs: number
): Promise<void> {
  try {
    await supabase.from('rule_evaluation_log').insert({
      payer_id: input.payer_id,
      plan_name: input.plan_name,
      cpt_codes: input.cpt_codes,
      modifiers: input.modifiers || [],
      injury_type: input.injury_type,
      site_of_service: input.site_of_service,
      diagnosis_codes: input.diagnosis_codes,
      matched_rule_ids: matchedRules.filter(r => r.rule_id !== 'default').map(r => r.rule_id),
      auth_requirement: authRequirement,
      required_documents: requiredDocuments,
      recommended_lane: recommendedLane,
      sla_hours: slaHours,
      follow_up_hours: followUpHours,
      evaluation_duration_ms: durationMs,
    });
  } catch (error) {
    // Don't fail the evaluation if logging fails
    console.error('Error logging evaluation:', error);
  }
}

/**
 * Get a simplified output for the surgeon console
 */
export function getSimplifiedOutput(output: RulesEngineOutput): {
  authRequired: 'Yes' | 'No' | 'Conditional';
  authCondition?: string;
  documentsNeeded: number;
  submissionMethod: string;
  estimatedDays: number;
} {
  const authLabels: Record<AuthRequirement, 'Yes' | 'No' | 'Conditional'> = {
    required: 'Yes',
    not_required: 'No',
    conditional: 'Conditional',
  };

  const laneLabels: Record<SubmissionLaneType, string> = {
    fhir_pas: 'Electronic (FHIR)',
    portal: 'Portal',
    fax: 'Fax',
    phone: 'Phone',
  };

  return {
    authRequired: authLabels[output.auth_required],
    authCondition: output.auth_condition_description,
    documentsNeeded: output.required_documents.filter(d => d.is_required).length,
    submissionMethod: laneLabels[output.recommended_lane],
    estimatedDays: Math.ceil(output.sla_hours / 24),
  };
}
